import { useState, useEffect, useCallback, useRef } from 'react';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import type { UserMediaEntity } from '../../../../../domain/entities/userMedia/userMediaEntity';
import { createFetchFeedUseCase } from '@/domain/usecases/feed/fetchFeedUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseFeedResult {
  medias: UserMediaEntity[];
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Initialize feed - call this from page lifecycle (onInit) */
  initialize: () => Promise<void>;
}

/**
 * Hook for managing feed data with infinite scroll
 * 
 * Business logic layer for FeedPage
 * - Orchestrates FetchFeedUseCase
 * - Manages local state (medias, loading, errors)
 * - Handles infinite scroll pagination
 * - Prevents duplicate items
 * 
 * Matches Flutter RootScreen._getStartupDataAndNavigateToMainScreen pattern
 */
export const useFeed = (): UseFeedResult => {
  const [medias, setMedias] = useState<UserMediaEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs to track current values without causing re-renders
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
  }, [isLoading, hasMore]);

  /**
   * Load more feed items (equivalent to fetchFeed call)
   */
  const loadMore = useCallback(async () => {
    // Check refs instead of state to avoid dependency issues
    if (isLoadingRef.current || !hasMoreRef.current) return;

    appLogger.debug('Fetching feed', 'useFeed.loadMore');

    setIsLoading(true);
    setError(null);

    try {
      // Create fresh usecase instance for each request (no singleton)
      const useCase = createFetchFeedUseCase();
      const response = await useCase.execute();

      setMedias(prevMedias => {
        // Prevent duplicates by filtering out existing IDs
        const existingIds = new Set(prevMedias.map(m => m.id));
        const newMedias = response.medias.filter((m: UserMediaEntity) => !existingIds.has(m.id));
        return [...prevMedias, ...newMedias];
      });

      setHasMore(response.hasMore);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load feed';
      setError(errorMessage);
      appLogger.error('Error loading feed', 'useFeed.loadMore', { error: err as Error });
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependencies - stable reference

  // Remove hidden content items from the feed
  useEffect(() => {
    return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
      setMedias(prev => prev.filter(m => m.id !== mediaId));
    });
  }, []);

  // Reactively update likes count when a user likes/unlikes a video
  useEffect(() => {
    return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
      setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, likes } : m));
    });
  }, []);

  // Reactively update comments count when a root comment is added
  useEffect(() => {
    return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
      setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, comments } : m));
    });
  }, []);

  // Reactively merge content item patches (publish-draft, schedule, link/unlink, …)
  useEffect(() => {
    return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
      setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, ...patch } : m));
    });
  }, []);

  /**
   * Refresh feed (clear and reload)
   */
  const refresh = useCallback(async () => {
    setMedias([]);
    setHasMore(true);
    setError(null);
    await loadMore();
  }, [loadMore]);

  /**
   * Initialize feed - call this from page lifecycle (e.g., onInit)
   * Provides explicit control over when initial data load happens
   */
  const initialize = useCallback(async () => {
    // Only initialize if no data exists yet
    if (medias.length === 0) {
      await loadMore();
    }
  }, [medias.length, loadMore]);

  return {
    medias,
    isLoading,
    hasMore,
    error,
    loadMore,
    refresh,
    initialize
  };
};